import { DashboardSiteInsights } from '@/api/apiService';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';

type Props = {
  insights: DashboardSiteInsights | null;
  bookingEnabled: boolean;
  leadCaptureEnabled: boolean;
  humanHandoffEnabled: boolean;
  askOutcomeEnabled: boolean;
  isPlatformAdmin?: boolean;
};

function formatMoney(amount: number, currency: string): string {
  const code = (currency || 'EUR').toUpperCase();
  try {
    return new Intl.NumberFormat(undefined, {
      style: 'currency',
      currency: code,
      maximumFractionDigits: 2,
    }).format(amount);
  } catch {
    return `${amount.toFixed(2)} ${code}`;
  }
}

function momLabel(momPct: number | null): string {
  if (momPct === null || Number.isNaN(momPct)) {
    return 'No prior month data';
  }
  if (momPct > 0) {
    return `↑ ${momPct}% vs last month`;
  }
  if (momPct < 0) {
    return `↓ ${Math.abs(momPct)}% vs last month`;
  }
  return 'Same as last month';
}

export default function DashboardInsightsCards({
  insights,
  bookingEnabled,
  leadCaptureEnabled,
  humanHandoffEnabled,
  askOutcomeEnabled,
  isPlatformAdmin = false,
}: Props) {
  if (!insights) {
    return null;
  }

  const bookings = insights.bookings;
  const value = insights.estimated_value;
  const activity = insights.activity || {
    leads_captured: 0,
    handoffs_completed: 0,
    reviews_positive: 0,
    outcomes_negative: 0,
    lead_to_booking_pct: null,
  };
  const pricedTotal = value.bookings_with_price + value.bookings_without_price;
  const multiCurrency = (value.by_currency?.length || 0) > 1;

  let bookingSubtitle: string;
  let valueSubtitle: string;
  let valueDisplay: string;

  if (!bookingEnabled) {
    bookingSubtitle = 'Enable booking to track appointments and value here';
    valueSubtitle = 'Enable booking to track appointments and value here';
    valueDisplay = '—';
  } else if (bookings.this_month === 0) {
    bookingSubtitle = momLabel(bookings.mom_pct);
    valueSubtitle = 'No bookings yet this month';
    valueDisplay = '—';
  } else if (value.bookings_with_price === 0) {
    bookingSubtitle = momLabel(bookings.mom_pct);
    valueSubtitle = 'Set service prices to see estimated value';
    valueDisplay = '—';
  } else {
    bookingSubtitle = momLabel(bookings.mom_pct);
    valueSubtitle = `${value.bookings_with_price} of ${pricedTotal} bookings had prices`;
    if (multiCurrency) {
      valueSubtitle += ' · multiple currencies';
    }
    valueDisplay = formatMoney(value.amount, value.currency);
  }

  const leadSubtitle = !leadCaptureEnabled
    ? 'Enable lead capture to track contacts here'
    : activity.lead_to_booking_pct !== null
      ? `${activity.lead_to_booking_pct}% lead → booking conversion`
      : 'Qualified contacts captured in chat';

  const handoffSubtitle = !humanHandoffEnabled
    ? 'Enable human handoff to track escalations'
    : 'Visitors who asked to speak with your team';

  const reviewSubtitle = !askOutcomeEnabled
    ? 'Enable post-appointment outcome emails to track this'
    : 'Yes clicks that open your Google review CTA';

  const negativeSubtitle = !askOutcomeEnabled
    ? 'Enable post-appointment outcome emails to track this'
    : 'Visitors who reported a poor appointment';

  return (
    <div className="space-y-3">
      <div>
        <h2 className="text-sm font-medium text-muted-foreground">This month</h2>
      </div>
      <div className="grid grid-cols-1 gap-3 sm:grid-cols-2 xl:grid-cols-4">
        <Card>
          <CardHeader className="pb-2">
            <CardTitle className="text-sm font-medium">Chat requests</CardTitle>
            <CardDescription>Messages handled by the assistant</CardDescription>
          </CardHeader>
          <CardContent className="text-2xl font-semibold">
            {insights.usage.request_count.toLocaleString()}
          </CardContent>
        </Card>

        <Card>
          <CardHeader className="pb-2">
            <CardTitle className="text-sm font-medium">Bookings this month</CardTitle>
            <CardDescription>{bookingSubtitle}</CardDescription>
          </CardHeader>
          <CardContent className="text-2xl font-semibold">
            {bookingEnabled ? bookings.this_month.toLocaleString() : '—'}
          </CardContent>
        </Card>

        <Card>
          <CardHeader className="pb-2">
            <CardTitle className="text-sm font-medium">Upcoming</CardTitle>
            <CardDescription>
              {bookingEnabled ? 'Appointments still ahead' : 'Enable booking to track appointments and value here'}
            </CardDescription>
          </CardHeader>
          <CardContent className="text-2xl font-semibold">
            {bookingEnabled ? bookings.upcoming.toLocaleString() : '—'}
          </CardContent>
        </Card>

        <Card>
          <CardHeader className="pb-2">
            <CardTitle className="text-sm font-medium">Estimated booked value (priced services only)</CardTitle>
            <CardDescription>{valueSubtitle}</CardDescription>
          </CardHeader>
          <CardContent className="text-2xl font-semibold">{valueDisplay}</CardContent>
        </Card>
      </div>

      <div className="grid grid-cols-1 gap-3 sm:grid-cols-2 xl:grid-cols-4">
        <Card>
          <CardHeader className="pb-2">
            <CardTitle className="text-sm font-medium">Leads captured</CardTitle>
            <CardDescription>{leadSubtitle}</CardDescription>
          </CardHeader>
          <CardContent className="text-2xl font-semibold">
            {leadCaptureEnabled ? activity.leads_captured.toLocaleString() : '—'}
          </CardContent>
        </Card>

        <Card>
          <CardHeader className="pb-2">
            <CardTitle className="text-sm font-medium">Human handoffs</CardTitle>
            <CardDescription>{handoffSubtitle}</CardDescription>
          </CardHeader>
          <CardContent className="text-2xl font-semibold">
            {humanHandoffEnabled ? activity.handoffs_completed.toLocaleString() : '—'}
          </CardContent>
        </Card>

        <Card>
          <CardHeader className="pb-2">
            <CardTitle className="text-sm font-medium">Reviews given</CardTitle>
            <CardDescription>{reviewSubtitle}</CardDescription>
          </CardHeader>
          <CardContent className="text-2xl font-semibold">
            {askOutcomeEnabled ? activity.reviews_positive.toLocaleString() : '—'}
          </CardContent>
        </Card>

        <Card>
          <CardHeader className="pb-2">
            <CardTitle className="text-sm font-medium">Negative outcomes</CardTitle>
            <CardDescription>{negativeSubtitle}</CardDescription>
          </CardHeader>
          <CardContent className="text-2xl font-semibold">
            {askOutcomeEnabled ? activity.outcomes_negative.toLocaleString() : '—'}
          </CardContent>
        </Card>
      </div>

      <div
        className={`grid grid-cols-1 gap-3 sm:grid-cols-2 ${
          isPlatformAdmin ? 'md:grid-cols-4' : 'md:grid-cols-3'
        }`}
      >
        <Card className="bg-muted/20">
          <CardHeader className="pb-2">
            <CardTitle className="text-sm font-medium text-muted-foreground">Total tokens</CardTitle>
          </CardHeader>
          <CardContent className="text-lg font-medium">
            {insights.usage.total_tokens.toLocaleString()}
          </CardContent>
        </Card>
        <Card className="bg-muted/20">
          <CardHeader className="pb-2">
            <CardTitle className="text-sm font-medium text-muted-foreground">Usage cost</CardTitle>
            <CardDescription>Estimated bill for this month</CardDescription>
          </CardHeader>
          <CardContent className="text-lg font-medium">
            {formatMoney(Number(insights.usage.billed_cost_usd || 0), 'USD')}
          </CardContent>
        </Card>
        {isPlatformAdmin ? (
          <Card className="bg-muted/20">
            <CardHeader className="pb-2">
              <CardTitle className="text-sm font-medium text-muted-foreground">
                Actual cost (admin)
              </CardTitle>
              <CardDescription>Estimated OpenAI spend at per-model rates</CardDescription>
            </CardHeader>
            <CardContent className="text-lg font-medium">
              {formatMoney(Number(insights.usage.actual_cost_usd || 0), 'USD')}
            </CardContent>
          </Card>
        ) : null}
        <Card className="bg-muted/20">
          <CardHeader className="pb-2">
            <CardTitle className="text-sm font-medium text-muted-foreground">Cancelled this month</CardTitle>
            <CardDescription>Bookings cancelled after being created</CardDescription>
          </CardHeader>
          <CardContent className="text-lg font-medium">
            {bookingEnabled ? bookings.cancelled_this_month.toLocaleString() : '—'}
          </CardContent>
        </Card>
      </div>
    </div>
  );
}
